import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PainParagraph = ({ text, delay, highlight }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.p
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.9, delay }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="text-lg md:text-xl font-light leading-relaxed transition-all duration-700"
      style={{
        color: hovered ? 'rgba(249,249,249,0.95)' : 'rgba(249,249,249,0.6)',
        textShadow: hovered && highlight ? '0 0 30px rgba(212,175,55,0.15)' : 'none',
      }}
    >
      {text}
    </motion.p>
  );
};

const DeepMirror = () => (
  <section className="relative py-36 bg-darker overflow-hidden border-t border-white/5">
    {/* Pulsing red glow at edges */}
    <motion.div
      animate={{ opacity: [0, 0.15, 0] }}
      transition={{ duration: 5, repeat: Infinity }}
      className="absolute inset-0 pointer-events-none"
      style={{ background: 'radial-gradient(ellipse at 0% 50%, rgba(114,47,55,0.3) 0%, transparent 40%), radial-gradient(ellipse at 100% 50%, rgba(114,47,55,0.3) 0%, transparent 40%)' }}
    />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,175,55,0.03)_0%,transparent_70%)]" />

    <div className="relative z-10 max-w-3xl mx-auto px-6">
      <motion.p
        initial={{ opacity: 0, letterSpacing: '0.1em' }}
        whileInView={{ opacity: 1, letterSpacing: '0.4em' }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
        className="text-wine-light uppercase text-xs font-semibold mb-12 text-center tracking-[0.35em]"
      >
        Der Spiegel der Wahrheit
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="text-4xl md:text-6xl font-serif text-cream italic leading-tight mb-16 text-center"
      >
        Ich sehe dich.{' '}
        <span className="text-wine-light block mt-2" style={{ textShadow: '0 0 20px rgba(155,66,78,0.35)' }}>
          Vollständig.
        </span>
      </motion.h2>

      <div className="space-y-10">
        <PainParagraph delay={0.1} text="Du liegst nachts wach. Das Handy liegt auf deiner Brust – nicht weil du schläfst, sondern weil du wartest. Auf eine Nachricht, die vielleicht kommt. Auf ein Zeichen, dass er an dich denkt. Auf die Bestätigung, dass das, was ihr habt, real ist und nicht nur in deinem Kopf existiert. Und während die Stunden vergehen, analysierst du jedes seiner letzten Worte, jeden Blick, jede Berührung – suchst nach einem versteckten Code, der dir sagt, dass du sicher bist. Dass du geliebt wirst. Dass du genug bist." />

        <PainParagraph delay={0.2} text="Oder du bist diejenige, die alles hat. Die erfolgreiche Unternehmerin, die Führungskraft, die Frau, die andere bewundern. Nach außen hin strahlt dein Leben wie poliertes Gold. Du hast die Umsätze, das Team, die Follower. Doch wenn die Kamera ausgeht und die Stille einsetzt, spürst du es: Diese bleierne Erschöpfung, die sich wie ein unsichtbares Gewicht auf deine Schultern legt. Du hast so viel gegeben – an dein Business, an andere, an den Erfolg – dass du nicht mehr weißt, wer du bist, wenn du nicht funktionierst." />

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative p-8 md:p-12"
          style={{ background: 'rgba(114,47,55,0.06)', border: '1px solid rgba(114,47,55,0.2)', borderLeft: '3px solid rgba(155,66,78,0.6)' }}
        >
          <p className="text-xl md:text-2xl font-serif text-cream italic leading-relaxed">
            "Du gibst. Du analysierst. Du versuchst es besser zu machen. Du passt dich an.
            Du wirst kleiner. Und trotzdem – die Reziprozität bleibt aus.
            Die Anerkennung bleibt aus. Die{' '}
            <span className="text-wine-light">tiefe, sichere Liebe</span> bleibt aus."
          </p>
        </motion.div>

        <PainParagraph delay={0.4} text="Du kennst dieses Gefühl, wenn du versuchst loszulassen – und merkst, dass Loslassen sich anfühlt wie Sterben. Als würdest du einen Teil von dir selbst verlieren, nicht nur ihn. Als wäre deine Identität so tief mit dieser Verbindung, mit diesem Warten, mit diesem Streben verwoben, dass du ohne es nicht mehr weißt, wer du bist. Das ist keine Schwäche. Das ist kein Versagen. Das ist das Zeichen, dass dein Nervensystem in einem Überlebensmodus gefangen ist, den es für Liebe hält." />

        <PainParagraph delay={0.5} highlight text="Und das Schmerzhafteste daran? Du weißt es. Tief in dir, unter all den Erklärungen und Rechtfertigungen, weißt du, dass sich etwas verändern muss. Nicht er. Nicht der Markt. Nicht der Algorithmus. Du. Und genau dieses Wissen – dieses leise, unbequeme Wissen – hat dich hierher geführt." />
      </div>

      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        whileInView={{ opacity: 1, scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, delay: 0.5 }}
        className="w-full h-[1px] mt-20 origin-center"
        style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.4), transparent)' }}
      />
    </div>
  </section>
);

export default DeepMirror;